// What the board shows, worked out from plain inputs: the review's state,
// the legal moves of its position, the board's two constraints and the
// arrows. Pure, so every lock and every square is tested without a board.
import type { ConstraintRegistry, ConstraintScope, NextMoveMode } from '@chess/api-constraints';
import type { Color, Fen, Move, Square } from '@chess/api-model';
import { headPly, isBrowsing, isExploring, lastMoveAt, positionAt, viewPly, type ReviewState } from './index.ts';
import type { BoardArrow, BoardLock, BoardView, PendingPromotion } from './board.ts';

export const START_FEN: Fen = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

/** The board's two constraints, read for its scope. */
export interface BoardConstraints {
  /** The Next move constraint. */
  nextMove: NextMoveMode;
  /** The Board arrows visibility constraint. */
  annotationsVisible: boolean;
}

/** The board's constraints in a scope; with no registry or scope, none. */
export function readBoardConstraints(registry: ConstraintRegistry | undefined, scope: ConstraintScope | undefined): BoardConstraints {
  if (!registry || !scope) return { nextMove: 'any', annotationsVisible: true };
  return {
    nextMove: registry.current(scope, 'nextMove').allow,
    annotationsVisible: registry.current(scope, 'boardArrowsVisibility').visible,
  };
}

/** The legal moves as the board wants them: from each square, where to. */
export function destsOf(moves: readonly Move[]): Map<Square, Square[]> {
  const dests = new Map<Square, Square[]>();
  for (const m of moves) {
    const list = dests.get(m.from);
    if (list) list.push(m.to);
    else dests.set(m.from, [m.to]);
  }
  return dests;
}

/** The square of a side's king in a position, read from its FEN. */
export function kingSquareOf(fen: Fen, color: Color): Square | undefined {
  const king = color === 'white' ? 'K' : 'k';
  const ranks = fen.split(' ')[0].split('/');
  for (let r = 0; r < ranks.length; r++) {
    let file = 0;
    for (const ch of ranks[r]) {
      if (ch >= '1' && ch <= '9') file += Number(ch);
      else {
        if (ch === king) return `${'abcdefgh'[file]}${8 - r}`;
        file++;
      }
    }
  }
  return undefined;
}

export interface BoardViewSources {
  state: ReviewState;
  /** The legal moves of the position on the board. */
  legal: readonly Move[];
  /** Whether the side to move is in check there. */
  inCheck?: boolean;
  constraints: BoardConstraints;
  /** The arrows the board would draw; dropped while the constraint hides them. */
  arrows?: readonly BoardArrow[];
  selected?: Square;
  pendingPromotion?: PendingPromotion;
}

function lockOf(state: ReviewState, fen: Fen | undefined, legal: readonly Move[], nextMove: NextMoveMode): BoardLock {
  if (!state.game || !fen) return 'noGame';
  if (isBrowsing(state)) return 'browsing';
  if (legal.length === 0) return 'gameOver';
  if (nextMove === 'none') return 'locked';
  if (nextMove === 'judged') return 'judged';
  return 'free';
}

/** Everything the board shows for a review state. */
export function reviewBoardView(sources: BoardViewSources): BoardView {
  const { state, legal, constraints } = sources;
  const position = positionAt(state);
  const fen = position ?? START_FEN;
  const turn: Color = fen.split(' ')[1] === 'b' ? 'black' : 'white';
  const lock = lockOf(state, position, legal, constraints.nextMove);
  const playable = lock === 'free' || lock === 'judged';
  const move = position ? lastMoveAt(state) : undefined;
  const check = position && sources.inCheck ? kingSquareOf(fen, turn) : undefined;
  const view: BoardView = {
    status: state.status,
    fen,
    orientation: state.game?.sides?.orientation ?? 'white',
    turn,
    dests: playable ? destsOf(legal) : new Map(),
    lock,
    annotationsVisible: constraints.annotationsVisible,
    arrows: constraints.annotationsVisible ? (sources.arrows ?? []) : [],
    path: { view: viewPly(state), head: headPly(state), exploring: isExploring(state), browsing: isBrowsing(state) },
  };
  if (move) view.lastMove = { from: move.from, to: move.to };
  if (check) view.check = check;
  if (playable) view.movable = turn;
  if (sources.selected !== undefined) view.selected = sources.selected;
  if (sources.pendingPromotion) view.pendingPromotion = sources.pendingPromotion;
  return view;
}
